/**
 * Context Class
 * 싱글톤 패턴으로 다국어 데이터 및 API URL 관리
 */
export class Context {
    static instance = null;
    
    constructor() {
        this.data = {};
        this.apiUrls = {};
        this.initialized = false;
    }

    /**
     * 싱글톤 인스턴스 가져오기
     */
    static getInstance() {
        if (!Context.instance) {
            Context.instance = new Context();
        }
        return Context.instance;
    }

    /**
     * Context 초기화 (서버에서 데이터 가져오기)
     */
    async init() {
        if (this.initialized) {
            return Promise.resolve();
        }

        try {
            const response = await fetch('/api/context', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success && result.data) {
                this.data = result.data.translations || {};
                this.apiUrls = result.data.apiUrls || {};
                this.initialized = true;
            } else {
                console.warn('Context initialization failed:', result.message);
            }
        } catch (error) {
            // 404 등: API 미구현 시 빈 데이터로 진행 (에러 로그 생략)
            if (typeof error?.message === 'string' && error.message.includes('404')) {
                this.initialized = true;
                return;
            }
            console.warn('Context initialization failed:', error?.message || error);
            this.initialized = true;
        }
    }

    /**
     * 데이터 가져오기
     * @param {string} key - 점(.)으로 구분된 키 (예: 'booking.title')
     * @param {*} defaultValue - 기본값
     */
    get(key, defaultValue = null) {
        if (!key) return defaultValue;

        const keys = key.split('.');
        let value = this.data;

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                return defaultValue;
            }
        }

        return value !== undefined ? value : defaultValue;
    }

    /**
     * API URL 가져오기
     * @param {string} key - API 키 (예: 'booking')
     */
    getApiUrl(key) {
        return this.apiUrls[key] || null;
    }

    /**
     * 모든 다국어 데이터 가져오기
     */
    getTranslations() {
        return this.data;
    }

    /**
     * 모든 API URL 가져오기
     */
    getApiUrls() {
        return this.apiUrls;
    }

    /**
     * 데이터 설정 (테스트용)
     */
    set(key, value) {
        const keys = key.split('.');
        let obj = this.data;

        for (let i = 0; i < keys.length - 1; i++) {
            const k = keys[i];
            if (!obj[k] || typeof obj[k] !== 'object') {
                obj[k] = {};
            }
            obj = obj[k];
        }

        obj[keys[keys.length - 1]] = value;
    }
}

/**
 * 전역 context 함수
 * @param {string} key - 다국어 키
 * @param {*} defaultValue - 기본값
 */
export function context(key, defaultValue = null) {
    return Context.getInstance().get(key, defaultValue);
}

// 전역으로 export
window.Context = Context;
window.context = context;

